import isEqual from 'lodash/isEqual';
import some from 'lodash/some';
import { DateTime } from 'luxon';

import { OpeningTime, WeeklySchedule } from 'apis/restaurant-api/merchant-admin/campaigns/types';

import { WeeklyScheduleEntries, Schedule, ScheduleTimeRange } from './types';

/**
 * It transforms every OpeningTime (open/closed) pairs into TimeRange (from/to) and sorts them
 * */
export const parseWeeklyScheduleToScheduleEntries = (weeklySchedule: WeeklySchedule): WeeklyScheduleEntries =>
  Object.entries(weeklySchedule).reduce<WeeklyScheduleEntries>((acc, [key, openingTimes]) => {
    if (openingTimes.length <= 0) {
      return acc;
    }

    return [
      ...acc,
      [
        key,
        openingTimes
          .reduce((_acc, openingTime, i) => {
            if (openingTime.type === 'close') {
              return _acc;
            }

            const fromDatetime = DateTime.fromISO(openingTime.value).setZone('UTC');
            const toDateTime = DateTime.fromISO(openingTimes[i + 1].value).setZone('UTC');

            return [
              ..._acc,
              createScheduleTimeRange({
                from: fromDatetime.hour * 60 + fromDatetime.minute,
                to: toDateTime.hour * 60 + toDateTime.minute,
              }),
            ];
          }, [])
          .sort((a, b) => {
            const fromSubtraction = a.from - b.from;

            if (fromSubtraction === 0) {
              return a.to - b.to;
            }

            return fromSubtraction;
          }),
      ],
    ];
  }, []);

/**
 * Merges duplicated ScheduleEntries by comparing TimeRange arrays
 * and parses input into Schedule array
 * */
export const parseScheduleEntriesToSchedules = (scheduleEntries: WeeklyScheduleEntries): Schedule[] =>
  scheduleEntries.reduce<Schedule[]>((acc, [__, currentTimeRanges]) => {
    // get duplicates for current entry
    const entryDuplicates = scheduleEntries.reduce((_acc, [key, timeRanges]) => {
      if (isEqual(currentTimeRanges, timeRanges)) {
        return [..._acc, key];
      }

      return _acc;
    }, []);

    const schedule = createSchedule({
      selectedDays: entryDuplicates,
      timeRanges: currentTimeRanges,
    });

    // check if current schedule already exists
    if (some(acc, schedule)) {
      return acc;
    }

    return [...acc, schedule];
  }, []);

export const parseWeeklyScheduleToSchedules = (weeklySchedule: WeeklySchedule): Schedule[] => {
  const scheduleEntries = parseWeeklyScheduleToScheduleEntries(weeklySchedule);
  return parseScheduleEntriesToSchedules(scheduleEntries);
};
export const createScheduleTimeRange = (overrides: Partial<ScheduleTimeRange> = {}) => ({
  from: 0,
  to: 60,
  ...overrides,
});

export const parseSchedulesToWeeklySchedule = (schedules: Schedule[]): WeeklySchedule =>
  schedules.reduce<WeeklySchedule>(
    (acc, value) => {
      value.selectedDays.forEach((day: Weekday) => {
        const openingTimes = value.timeRanges.reduce<OpeningTime[]>((_acc, timeRange) => {
          return [
            ..._acc,
            {
              type: 'open' as const,
              value: DateTime.fromMillis(convertMinutesToMilliseconds(timeRange.from)).setZone('UTC').toISO(),
            },
            {
              type: 'close' as const,
              value: DateTime.fromMillis(convertMinutesToMilliseconds(timeRange.to)).setZone('UTC').toISO(),
            },
          ];
        }, [] as OpeningTime[]);

        acc[day].push(...openingTimes);
      });

      return acc;
    },
    {
      monday: [],
      tuesday: [],
      wednesday: [],
      thursday: [],
      friday: [],
      saturday: [],
      sunday: [],
    },
  );

export const createSchedule = (overrides: Partial<Schedule> = {}): Schedule => ({
  selectedDays: [],
  timeRanges: [createScheduleTimeRange()],
  ...overrides,
});

export const convertMinutesToMilliseconds = (minutes: number) => minutes * 60 * 1000;

export const getLocalizedWeekDay = (weekDayIdx: number, locale: string) => {
  const { format } = new Intl.DateTimeFormat(locale, { weekday: 'long' });
  const day = format(new Date(Date.UTC(1970, 0, 5 + weekDayIdx)));

  return `${day.charAt(0).toUpperCase()}${day.slice(1)}`;
};

export const getDuration = (startDate: string, endDate: string, timezone: string) => {
  const startDateObj = DateTime.fromISO(startDate).set({ second: 0, millisecond: 0 }).setZone(timezone);
  const endDateObj = DateTime.fromISO(endDate).set({ second: 0, millisecond: 0 }).setZone(timezone);
  const duration = endDateObj.diff(startDateObj, ['days', 'hours', 'minutes']);
  const durationObj = duration.toObject();

  return {
    value: durationObj.days ? durationObj.days : durationObj.hours ? durationObj.hours : durationObj.minutes ?? 0,
    unit: durationObj.days ? 'day' : durationObj.hours ? 'hour' : 'minute',
  };
};
